import {
  Badge,
  Button,
  Checkbox,
  ScrollArea,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import clsx from 'clsx';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { AnimatedRoutedDrawer } from 'src/Components/Drawers/AnimatedRoutedDrawer';
import { useCreateHousehold, useUpdateHousehold } from 'src/Hooks/Mutations/useHouseholdMutations';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import useFetchEligibleHouseholdMembers from 'src/Hooks/Queries/useFetchEligibleHouseholdMembers';
import useFetchHouseholds from 'src/Hooks/Queries/useFetchHouseholds';
import { AssetGovernanceFields, OperationalRole, ShareType } from 'src/types/governance.types';
import { HouseholdCostBearerMode } from 'src/types/household.types';
import { OWNERSHIP_MANTINE_COLOR, OWNERSHIP_SHORT_LABEL } from 'src/Utils/ownershipColors';

import styles from './HouseholdFormDrawer.module.scss';

const formatPct = (pct: number): string => {
  const rounded = Math.round(pct * 10) / 10;
  return `${rounded}%`;
};

const HOUSEHOLD_COLORS = [
  '#2e7d6c',
  '#1976d2',
  '#9c27b0',
  '#e64a19',
  '#fbc02d',
  '#5d4037',
  '#455a64',
  '#c2185b',
];

interface FormValues {
  name: string;
  color: string;
  costBearerMode: HouseholdCostBearerMode;
  memberIds: string[];
  headMemberId: string | null;
}

const HouseholdFormDrawerContent = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const { householdId } = useParams<{ householdId?: string }>();
  const { data: households } = useFetchHouseholds();
  const { data: eligibleMembers } = useFetchEligibleHouseholdMembers();
  const { data: asset } = useFetchAssetDetails();
  const governance = asset as unknown as AssetGovernanceFields | undefined;
  const shareType = governance?.shareType;

  const household = householdId ? households?.find(h => h.id === householdId) : undefined;
  const isEditing = !!householdId;

  const { mutate: createHousehold, isLoading: isCreating } = useCreateHousehold({
    onSuccess: onClose,
  });
  const { mutate: updateHousehold, isLoading: isUpdating } = useUpdateHousehold({
    onSuccess: onClose,
  });
  const isLoading = isCreating || isUpdating;

  const form = useForm<FormValues>({
    initialValues: {
      name: '',
      color: HOUSEHOLD_COLORS[0],
      costBearerMode: HouseholdCostBearerMode.REPRESENTATIVE,
      memberIds: [],
      headMemberId: null,
    },
    validate: {
      name: value =>
        value.trim().length === 0
          ? t('admin.households.form.errors.nameRequired', 'Le nom est requis')
          : null,
      memberIds: value =>
        value.length === 0
          ? t('admin.households.form.errors.membersRequired', 'Au moins un membre est requis')
          : null,
    },
  });

  useEffect(() => {
    if (household) {
      form.setValues({
        name: household.name,
        color: household.color,
        costBearerMode: household.costBearerMode,
        memberIds: household.members.map(m => m.id),
        headMemberId: household.head?.id ?? null,
      });
    } else {
      form.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [householdId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedMembers = useMemo(
    () => (eligibleMembers ?? []).filter(m => form.values.memberIds.includes(m.id)),
    [eligibleMembers, form.values.memberIds]
  );

  // Un représentant n'a pas besoin d'être inscrit : le back sait faire porter la part
  // du foyer à un membre non-inscrit, ce qui débloque le remboursement de ses opérations.
  const headSelectData = selectedMembers.map(m => {
    const name =
      [m.firstName, m.lastName].filter(Boolean).join(' ') ||
      t('admin.households.form.unnamedMember', 'Membre sans nom');

    return {
      value: m.id,
      label: m.isPlaceholder
        ? t('admin.households.form.headNotRegisteredOption', {
            name,
            defaultValue: '{{name}} (non inscrit)',
          })
        : name,
    };
  });

  const handleSubmit = (values: FormValues) => {
    const payload = {
      name: values.name.trim(),
      color: values.color,
      costBearerMode: values.costBearerMode,
      memberIds: values.memberIds,
      headMemberId: values.headMemberId ?? undefined,
    };
    if (isEditing && householdId) {
      updateHousehold({ householdId, payload });
    } else {
      createHousehold(payload);
    }
  };

  return (
    <form className={styles.formStack} onSubmit={form.onSubmit(handleSubmit)}>
      <TextInput
        label={t('admin.households.form.nameLabel', 'Nom du foyer')}
        placeholder={t('admin.households.form.namePlaceholder', 'Ex. Famille Gautier')}
        withAsterisk
        {...form.getInputProps('name')}
      />

      <div>
        <div className={styles.label}>{t('admin.households.form.colorLabel', 'Couleur')}</div>
        <div className={styles.colorRow}>
          {HOUSEHOLD_COLORS.map(color => (
            <button
              type="button"
              key={color}
              className={clsx(
                styles.colorSwatch,
                form.values.color === color && styles.colorSwatchSelected
              )}
              style={{ backgroundColor: color }}
              onClick={() => form.setFieldValue('color', color)}
              aria-label={color}
            />
          ))}
        </div>
      </div>

      <div className={styles.membersSection}>
        <div className={styles.label}>{t('admin.households.form.membersLabel', 'Membres')}</div>
        <ScrollArea.Autosize mah={200}>
          <Stack gap={4}>
            {(eligibleMembers ?? []).map(member => {
              const isVisitor = member.operationalRole === OperationalRole.VISITEUR;
              const label =
                [member.firstName, member.lastName].filter(Boolean).join(' ') ||
                t('admin.households.form.unnamedMember', 'Membre sans nom');
              const isInOtherHousehold = !!member.householdId && member.householdId !== householdId;
              const checkbox = (
                <Checkbox
                  key={member.id}
                  checked={form.values.memberIds.includes(member.id)}
                  disabled={isInOtherHousehold}
                  onChange={event => {
                    const checked = event.currentTarget.checked;
                    const next = checked
                      ? [...form.values.memberIds, member.id]
                      : form.values.memberIds.filter(id => id !== member.id);
                    form.setFieldValue('memberIds', next);
                    if (!checked && form.values.headMemberId === member.id) {
                      form.setFieldValue('headMemberId', null);
                    }
                  }}
                  label={
                    <Text size="sm" c={isInOtherHousehold ? 'dimmed' : undefined}>
                      {label}
                      {member.isAssetOwner && (
                        <Text span size="xs" c="dimmed" ml={6}>
                          {t('admin.households.form.ownerBadge', 'Propriétaire')}
                        </Text>
                      )}
                      {isVisitor && (
                        <Text span size="xs" c="dimmed" ml={6}>
                          {t('admin.households.form.visitorBadge', 'Visiteur')}
                        </Text>
                      )}
                      {member.ownershipNature && (
                        <Badge
                          size="xs"
                          variant="light"
                          color={OWNERSHIP_MANTINE_COLOR[member.ownershipNature]}
                          ml={6}
                        >
                          {OWNERSHIP_SHORT_LABEL[member.ownershipNature]}{' '}
                          {formatPct(member.sharesPct ?? 0)}
                          {shareType !== ShareType.PERCENTAGE && member.shares != null
                            ? ` · ${member.shares}`
                            : ''}
                        </Badge>
                      )}
                    </Text>
                  }
                />
              );
              if (isInOtherHousehold) {
                return (
                  <Tooltip
                    key={member.id}
                    label={t('admin.households.form.memberInOtherHousehold', {
                      name: member.householdName,
                      defaultValue: 'Déjà dans le foyer "{{name}}"',
                    })}
                    withArrow
                    position="right"
                  >
                    <div>{checkbox}</div>
                  </Tooltip>
                );
              }
              return checkbox;
            })}
            {eligibleMembers?.length === 0 && (
              <Text size="sm" c="dimmed">
                {t('admin.households.form.noMembers', 'Aucun membre sur ce bien pour le moment.')}
              </Text>
            )}
          </Stack>
        </ScrollArea.Autosize>
        {form.errors.memberIds && (
          <Text size="xs" c="red" mt={4}>
            {form.errors.memberIds}
          </Text>
        )}
      </div>

      <Select
        label={t('admin.households.form.headLabel', 'Représentant du foyer')}
        placeholder={t('admin.households.form.headPlaceholder', 'Choisir un membre du foyer')}
        data={headSelectData}
        value={form.values.headMemberId}
        onChange={value => form.setFieldValue('headMemberId', value)}
        disabled={selectedMembers.length === 0}
        clearable
      />
      <Text size="xs" c="dimmed" mt={4}>
        {t(
          'admin.households.form.headHelp',
          'Optionnel. Si "Représentant" est choisi pour les frais, c\'est lui qui paiera la part familiale. Un membre non inscrit peut être représentant.'
        )}
      </Text>

      <div>
        <div className={styles.label}>
          {t('admin.households.form.costBearerLabel', 'Qui porte les frais ?')}
        </div>
        <SegmentedControl
          fullWidth
          value={form.values.costBearerMode}
          onChange={value => form.setFieldValue('costBearerMode', value as HouseholdCostBearerMode)}
          data={[
            {
              value: HouseholdCostBearerMode.REPRESENTATIVE,
              label: t('admin.households.form.costBearerRepresentative', 'Représentant'),
            },
            {
              value: HouseholdCostBearerMode.OWNERS_SPLIT,
              label: t(
                'admin.households.form.costBearerOwnersSplit',
                'Partagé entre propriétaires'
              ),
            },
          ]}
        />
        <Text size="xs" c="dimmed" mt={6}>
          {t(
            'admin.households.form.costBearerHelp',
            'Utilisé quand une dépense est répartie par foyer pour décider qui paie la part du foyer.'
          )}
        </Text>
      </div>

      <div className={styles.buttonGroup}>
        <div className={styles.actions}>
          <Button variant="light" onClick={onClose}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button type="submit" loading={isLoading}>
            {isEditing ? t('common.save', 'Enregistrer') : t('common.create', 'Créer')}
          </Button>
        </div>
      </div>
    </form>
  );
};

const HouseholdFormDrawer = () => {
  const { t } = useTranslation();
  const { householdId, assetId } = useParams<{ householdId?: string; assetId: string }>();

  return (
    <AnimatedRoutedDrawer
      title={
        householdId
          ? t('admin.households.form.titleEdit', 'Modifier le foyer')
          : t('admin.households.form.titleCreate', 'Créer un foyer')
      }
      to={`/asset/${assetId}/admin?tab=membres`}
    >
      {({ onClose }) => <HouseholdFormDrawerContent onClose={onClose} />}
    </AnimatedRoutedDrawer>
  );
};

export default HouseholdFormDrawer;
